import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Leaf } from "lucide-react";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { toast } from "sonner";

const ROLES = ["Auto-entrepreneur", "Fondateur", "CEO", "Président", "DG", "Responsable Événementiel", "Chargé de Communication", "Responsable Marketing", "DAF", "Autre"];
const ORG_TYPES = ["Entreprise", "Agence événementielle", "Collectivité", "Université / Centre de formation", "Association"];

function Field({ label, children }) {
  return (<div><Label className="mb-1.5 block">{label}</Label>{children}</div>);
}

export default function Profile() {
  const { profile, company, saveProfile, saveCompany } = useData();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState({ firstName: "", lastName: "", email: "", role: "", phone: "" });
  const [c, setC] = useState({ companyName: "", orgType: "", siret: "", vat: "", headquartersAddress: "", headquartersCity: "", headquartersPostal: "" });

  useEffect(() => {
    setP((prev) => ({ ...prev, ...profile, email: profile.email || user?.email || "" }));
    setC((prev) => ({ ...prev, ...company }));
  }, [profile, company, user]);

  const REQUIRED_MSG = "Merci de compléter tous les champs obligatoires (profil et organisme).";
  const save = async () => {
    const missP = !p.firstName?.trim() || !p.lastName?.trim() || !p.email?.trim() || !p.role || !p.phone?.trim();
    const missC = !c.companyName?.trim() || !c.orgType || !c.siret?.trim() || !c.vat?.trim() || !c.headquartersAddress?.trim() || !c.headquartersCity?.trim() || !c.headquartersPostal?.trim();
    if (missP || missC) { toast.error(REQUIRED_MSG); return; }
    await Promise.all([saveProfile(p), saveCompany(c)]);
    toast.success("Profil enregistré");
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-background py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-emerald-400 flex items-center justify-center">
            <Leaf className="h-5 w-5 text-white" />
          </div>
          <span className="font-display font-extrabold text-xl">JustPlanIt</span>
        </div>
        <div className="bg-card rounded-2xl border border-border shadow-sm p-6 sm:p-8">
          <h1 className="font-display text-2xl font-bold mb-1">Créer votre profil organisateur</h1>
          <p className="text-muted-foreground text-sm mb-8">Ces informations personnalisent votre espace et vos documents.</p>

          <h3 className="font-display font-semibold mb-4 text-indigo-600">Identité</h3>
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <Field label="Prénom"><Input data-testid="profile-firstname" value={p.firstName} onChange={(e) => setP({ ...p, firstName: e.target.value })} /></Field>
            <Field label="Nom"><Input data-testid="profile-lastname" value={p.lastName} onChange={(e) => setP({ ...p, lastName: e.target.value })} /></Field>
            <Field label="Email"><Input type="email" data-testid="profile-email" value={p.email} onChange={(e) => setP({ ...p, email: e.target.value })} /></Field>
            <Field label="Rôle">
              <Select value={p.role} onValueChange={(v) => setP({ ...p, role: v })}>
                <SelectTrigger data-testid="profile-role"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Téléphone"><Input data-testid="profile-phone" value={p.phone} onChange={(e) => setP({ ...p, phone: e.target.value })} placeholder="06 12 34 56 78" /></Field>
          </div>

          <h3 className="font-display font-semibold mb-4 text-indigo-600">Organisme</h3>
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <Field label="Raison sociale"><Input data-testid="profile-company" value={c.companyName} onChange={(e) => setC({ ...c, companyName: e.target.value })} /></Field>
            <Field label="Type d'organisme">
              <Select value={c.orgType} onValueChange={(v) => setC({ ...c, orgType: v })}>
                <SelectTrigger data-testid="profile-orgtype"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>{ORG_TYPES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="SIRET"><Input data-testid="profile-siret" value={c.siret} onChange={(e) => setC({ ...c, siret: e.target.value })} /></Field>
            <Field label="N° TVA"><Input data-testid="profile-vat" value={c.vat} onChange={(e) => setC({ ...c, vat: e.target.value })} /></Field>
          </div>

          <h3 className="font-display font-semibold mb-4 text-indigo-600">Siège social</h3>
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <div className="sm:col-span-2"><Field label="Adresse (vérifiée par Google Maps)">
              <AddressAutocomplete testid="profile-address" value={c.headquartersAddress} city={c.headquartersCity}
                onChange={(v) => setC({ ...c, headquartersAddress: v })}
                onResolved={(r) => setC((prev) => ({ ...prev, headquartersAddress: r.formatted, headquartersCity: r.city || prev.headquartersCity, headquartersPostal: r.postal || prev.headquartersPostal }))} />
            </Field></div>
            <Field label="Ville"><Input data-testid="profile-city" value={c.headquartersCity} onChange={(e) => setC({ ...c, headquartersCity: e.target.value })} /></Field>
            <Field label="Code postal"><Input data-testid="profile-postal" value={c.headquartersPostal} onChange={(e) => setC({ ...c, headquartersPostal: e.target.value })} /></Field>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => navigate("/dashboard")} data-testid="profile-cancel">Annuler</Button>
            <Button onClick={save} data-testid="profile-save">Enregistrer le profil</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
